import React, { useEffect, useState } from "react";
import axios from "axios";

// Drawer (panel lateral) que muestra el historial de UNA fila concreta
// (un alumno o un piso), como timeline vertical. Se usa desde el botón
// "🕓 Historial" de cada fila en AdminGestionAlumnos.js / AdminGestionPisos.js.

const EVENTO_INFO = {
  ALTA: { label: "Alta", emoji: "🆕", bg: "#e6f4ea", color: "#1e7e34", border: "#b7e1c1" },
  BAJA: { label: "Baja", emoji: "🚫", bg: "#fdecea", color: "#c0392b", border: "#f3c2bd" },
  MODIFICACION: { label: "Modificado", emoji: "✏️", bg: "#fff8e1", color: "#8a6d00", border: "#f0e2a3" },
};

const ETIQUETAS_CAMPO = {
  id_alumno: "ID alumno", id_piso: "Piso", habitacion: "Habitación", duracion: "Duración",
  pais: "País", a_r: "A/R", activo: "Activo", fecha_baja: "Fecha de baja",
  abreviatura: "Abreviatura", plazas_ocupadas: "Plazas ocupadas", empresa: "Empresa",
  sexo: "Sexo", servicio: "Servicio", horas_dia: "Horas/día", horas_semana: "Horas/semana",
  user_supervisora: "Supervisora", user_comprador: "Comprador", id_asistenta: "ID asistenta",
  nombre_asistenta: "Asistenta", ruta: "Ruta", supermercado: "Supermercado",
  franja_horaria: "Franja horaria", nombre: "Nombre",
};

function humanizarClave(k) {
  if (ETIQUETAS_CAMPO[k]) return ETIQUETAS_CAMPO[k];
  return k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function formatFecha(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

function DescripcionEvento({ ev }) {
  if (ev.tipo_evento === "MODIFICACION") {
    return (
      <p style={styles.linea}>
        <strong>{humanizarClave(ev.campo || "")}</strong>: {ev.valor_anterior || "—"} → {ev.valor_nuevo || "—"}
      </p>
    );
  }
  return <p style={styles.linea}>{ev.tipo_evento === "ALTA" ? "Registro creado." : "Dado de baja."}</p>;
}

const HistorialFilaDrawer = ({ abierto, endpoint, filaId, titulo, onCerrar }) => {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!abierto || !filaId) return;
    setLoading(true);
    setError("");
    axios
      .get(endpoint, { params: { fila_id: filaId, per_page: 100, orden: "fecha_hora", dir: "desc" } })
      .then((res) => setEventos(res.data?.rows || []))
      .catch(() => setError("❌ No se pudo cargar el historial."))
      .finally(() => setLoading(false));
  }, [abierto, endpoint, filaId]);

  useEffect(() => {
    if (!abierto) return;
    const onKey = (e) => { if (e.key === "Escape") onCerrar(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [abierto, onCerrar]);

  if (!abierto) return null;

  return (
    <div style={styles.overlay} onClick={onCerrar}>
      <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <h3 style={styles.titulo}>🕓 Historial</h3>
            <p style={styles.subtitulo}>{titulo}</p>
          </div>
          <button style={styles.cerrar} onClick={onCerrar} aria-label="Cerrar" title="Cerrar (Esc)">✕</button>
        </div>

        <div style={styles.body}>
          {loading ? (
            <p style={{ textAlign: "center", color: "#78909c" }}>Cargando...</p>
          ) : error ? (
            <p style={{ textAlign: "center", color: "#c0392b" }}>{error}</p>
          ) : eventos.length === 0 ? (
            <p style={{ textAlign: "center", color: "#78909c" }}>Sin eventos registrados todavía.</p>
          ) : (
            <div style={styles.timeline}>
              {eventos.map((ev) => {
                const info = EVENTO_INFO[ev.tipo_evento] || EVENTO_INFO.MODIFICACION;
                return (
                  <div key={ev.id} style={styles.evento}>
                    <div style={{ ...styles.punto, background: info.color }} />
                    <div style={styles.eventoContenido}>
                      <div style={styles.eventoCabecera}>
                        <span style={{ ...styles.badge, background: info.bg, color: info.color, border: `1px solid ${info.border}` }}>
                          {info.emoji} {info.label}
                        </span>
                        <span style={styles.fecha}>{formatFecha(ev.fecha_hora)}</span>
                      </div>
                      <DescripcionEvento ev={ev} />
                      <p style={styles.editor}>👤 {ev.editor_email || "desconocido"}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(15, 23, 42, 0.45)", zIndex: 1000, display: "flex", justifyContent: "flex-end",
  },
  panel: {
    width: "min(440px, 92vw)", height: "100%", background: "#fff",
    boxShadow: "-8px 0 24px rgba(0,0,0,0.25)", display: "flex", flexDirection: "column",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    padding: "18px 20px", borderBottom: "1px solid #eee", background: "#f7f9fa",
  },
  titulo: { margin: 0, fontSize: 17, color: "#263238" },
  subtitulo: { margin: "4px 0 0", fontSize: 13, color: "#455a64", fontWeight: 600 },
  cerrar: {
    border: "none", background: "#eceff1", color: "#455a64", width: 32, height: 32,
    borderRadius: "50%", cursor: "pointer", fontSize: 15, fontWeight: 700, flexShrink: 0,
  },
  body: { padding: "20px 22px", overflowY: "auto", flex: 1 },
  timeline: { position: "relative", display: "flex", flexDirection: "column", gap: 20 },
  evento: { display: "flex", gap: 14 },
  punto: { width: 12, height: 12, borderRadius: "50%", marginTop: 6, flexShrink: 0 },
  eventoContenido: {
    flex: 1, background: "#f7f9fa", border: "1px solid #eef1f3", borderRadius: 12, padding: "12px 14px",
  },
  eventoCabecera: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 6 },
  badge: { padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" },
  fecha: { fontSize: 12, color: "#78909c" },
  linea: { margin: "4px 0", fontSize: 13.5, color: "#263238" },
  editor: { margin: "4px 0 0", fontSize: 11.5, color: "#90a4ae" },
};

export default HistorialFilaDrawer;
